import { Destroyable } from '../common';
import { GeneratorRepository } from './generator.repository';

/**
 * 设计时和运行时都会使用到GeneratorManager
 * 管理设计时或运行时加载的全部数据源生成器
 *
 * 由于权限管理的问题，不同用户群可以访问的组件库和数据源不尽相同，所以ComponentRepositoryManager不再是单例模式。
 * 一个manager可以管理多个Repository，一个Repository可以包含在多个manager中。方便数据源生成器的权限管理
 *
 */
// @dynamic
export class GeneratorManager extends Destroyable {
  private static _manager: GeneratorManager;

  private _repositoryMap: Map<string, GeneratorRepository> = new Map();

  static getInstance() {
    if (!this._manager) {
      this._manager = new GeneratorManager();
    }
    return this._manager;
  }


  private constructor() {
    super();

    this.onDestroy(() => {
      // repository可能包含在其他manager中，因此这里只需要解除对repository的引用，无需销毁；
      this._repositoryMap.clear();
      this._repositoryMap = null;
    });
  }

  addRepository(repo: GeneratorRepository | Array<GeneratorRepository>) {
    if (this.usable) {
      if (repo instanceof GeneratorRepository) {
        this._repositoryMap.set(repo.id, repo);
      } else {
        repo.forEach(item => this._repositoryMap.set(item.id, item));
      }

    }
  }

  removeRepository(repo: GeneratorRepository | Array<GeneratorRepository>) {
    if (this.usable) {
      if (repo instanceof GeneratorRepository) {
        this._repositoryMap.delete(repo.id);
      } else {
        repo.forEach(value => this._repositoryMap.delete(value.id));
      }

    }
  }

  clear() {
    if (this.usable) {
      this._repositoryMap.clear();
    }
  }

  /**
   * 根据ID获得数据源生成器仓库
   * @param id
   */
  getRepository(id: string): GeneratorRepository {
    return this._repositoryMap.get(id);
  }

  /**
   * 获得当前manager中的所有Repositories
   */
  get repositories(): Array<GeneratorRepository> {
    return Array.from(this._repositoryMap.values());
  }

  /**
   * 判断是否支持指定的数据源生成器库
   * @param repoIDArray
   */
  includes(repoIDArray: Array<string>): boolean {
    const keys = Array.from(this._repositoryMap.keys());
    return repoIDArray.every((value) => {
      return keys.includes(value);
    });
  }

  /**
   * 给定路径的数据源生成器是否存在
   * @param path
   */
  has(path: string) {
    const [repoKey, geneKey] = path.split('$');
    return this._repositoryMap.has(repoKey) ? this._repositoryMap.get(repoKey).has(geneKey) : false;
  }

  /**
   * 根据路径获得数据源生成器  例如：repo1$mockDataSource
   * repo1  数据源生成器仓库名称
   * mockDataSource 数据源生成器ID
   * @param path
   */
  getGenerator(path: string) {
    const [repoID, geneID] = path.split('$');
    if (this._repositoryMap.has(repoID)) {
      return this._repositoryMap.get(repoID).getGenerator(geneID);
    }
  }

  get meta() {
    return Array.from(this._repositoryMap.entries()).map((([key, value], index) => {
      return {
        label: key,
        children: value.meta
      };
    }));
  }
}

export const generatorManager = GeneratorManager.getInstance();
